<template>
  <div class="buy-confirm">
    <div class="confirmation-container" v-if="!loading">
      <div v-if="success" class="success-message">
        <span class="material-icons success-icon fa-check-circle"></span>
        <h2>{{ $t('purchase.buyConfirm.success.title') }}</h2>
        <p>{{ $t('purchase.buyConfirm.success.received', { paralleliums }) }}</p>
        <p>{{ $t('purchase.buyConfirm.success.total', { total: totalParalleliums }) }}</p>
        <button class="return-button" @click="returnToGame">
          {{ $t('purchase.buyConfirm.buttons.return') }}
        </button>
      </div>
      <div v-else class="error-message">
        <span class="material-icons error-icon fa-times-circle"></span>
        <h2>{{ $t('purchase.buyConfirm.error.title') }}</h2>
        <p>{{ errorMessage }}</p>
        <button class="return-button" @click="returnToGame">
          {{ $t('purchase.buyConfirm.buttons.tryAgain') }}
        </button>
      </div>
    </div>
    <div v-else class="loading">
      <div class="spinner"></div>
      <p>{{ $t('purchase.buyConfirm.loading') }}</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { trackPurchase } from '@/plugins/analytics';

export default {
  name: 'BuyConfirm',
  data() {
    return {
      loading: true,
      success: false,
      paralleliums: 0,
      totalParalleliums: 0,
      errorMessage: ''
    }
  },
  async created() {
    try {
      const sessionId = new URLSearchParams(window.location.search).get('session_id');
      if (!sessionId) {
        throw new Error(this.$t('purchase.buyConfirm.error.sessionError'));
      }

      const result = await this.confirmPayment(sessionId);
      
      if (result && result.success && result.paralleliums) {
        this.success = true;
        this.paralleliums = result.paralleliums;
        this.totalParalleliums = result.totalParalleliums || result.paralleliums;
        
        const purchaseData = {
          orderId: sessionId,
          total: this.totalParalleliums,
          items: [{
            id: 'paralleliums',
            name: 'Paralleliums',
            price: result.paralleliums,
            quantity: 1
          }]
        };
        
        // Wrap in try-catch to prevent analytics errors from breaking the flow
        try {
          await trackPurchase(purchaseData);
        } catch (analyticsError) {
          console.warn('Analytics tracking failed:', analyticsError);
        }
      } else {
        throw new Error(this.$t('purchase.buyConfirm.error.confirmationFailed'));
      }
    } catch (error) {
      this.success = false;
      this.errorMessage = error.message || this.$t('purchase.buyConfirm.error.message');
      console.error('Payment confirmation error:', error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions({
      confirmPayment: 'player/confirmPayment'
    }),
    returnToGame() {
      this.$router.push('/home');
    }
  }
}
</script>

<style scoped>
.buy-confirm {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #1a2634 0%, #2c3e50 100%);
}

.confirmation-container {
  background: rgba(0, 0, 0, 0.3);
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 2px solid #4a90e2;
  max-width: 400px;
  width: 90%;
}

.success-icon {
  font-size: var(--f-7);
  color: #2ecc71;
  margin-bottom: 1rem;
}

.error-icon {
  font-size: var(--f-7);
  color: #e74c3c;
  margin-bottom: 1rem;
}

h2 {
  color: #ecf0f1;
  margin-bottom: 1rem;
}

p {
  color: #bdc3c7;
  margin-bottom: 1rem;
}

.return-button {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: transform 0.3s ease, opacity 0.3s ease, background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
}
@media (hover: hover) {
  .return-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
  }
}

.loading {
  text-align: center;
  color: #ecf0f1;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #3498db;
  border-top: 5px solid transparent;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
